import type { Constructor, Container, Operation } from '@packages/core/container';

import type { WorksheetFormEntity } from '../../../worksheets/forms/entities';
import type { WorksheetSubmissionGroupEntity } from '../entities';

import { FetchAllSubmissions } from './fetch-all-submissions';
import { FetchGroups } from './fetch-groups';

/** Parameters for refreshing worksheet submission data after a mutation. */
export type RefreshSubmissionsParams = {
    /** The case number to refresh worksheet data for. */
    caseNumber: string;
};

/**
 * Result of refreshing worksheet submission data. Contains both the fresh
 * group metadata and the full submission form data for every submission
 * in the case.
 */
export type RefreshSubmissionsResult = {
    /** The refreshed worksheet submission groups from NorthStar, or undefined if the fetch failed. */
    groups: WorksheetSubmissionGroupEntity[] | undefined;

    /**
     * All worksheet submissions for the case, keyed by submission ID.
     * Empty map if there are no submissions or if the groups fetch failed.
     */
    submissionMap: Map<string, WorksheetFormEntity>;
};

/**
 * Composite operation that fetches fresh worksheet submission groups from NorthStar
 * and then bulk-fetches all individual submissions in parallel. Designed to run after
 * any worksheet mutation (save, submit, delete) so the caller receives a complete,
 * consistent snapshot of the current state in a single awaitable call.
 *
 * Delegates to {@link FetchGroups} and {@link FetchAllSubmissions} rather than
 * calling the API client directly, keeping DTO-to-entity mapping in one place.
 */
export class RefreshSubmissions implements Operation {
    /**
     * Register this operation with its sub-operation dependencies. Both
     * sub-operations must already be registered in the container (they
     * self-register via the barrel import scan in registerDomainPackage).
     *
     * @param container - the container to register in
     */
    static registerInContainer(container: Container): void {
        container.register(RefreshSubmissions as unknown as Constructor, [FetchGroups, FetchAllSubmissions]);
    }

    /**
     * @param fetchGroupsOp - operation that fetches worksheet submission groups from case details
     * @param fetchAllSubmissionsOp - operation that bulk-fetches individual submissions in parallel
     */
    constructor(
        private fetchGroupsOp: FetchGroups,
        private fetchAllSubmissionsOp: FetchAllSubmissions
    ) {}

    /**
     * Fetch fresh worksheet groups, extract submission IDs, and bulk-fetch
     * all submissions in parallel. Returns both the groups and a complete
     * submission map so the caller can update all state atomically.
     *
     * @param params - the case number to refresh
     * @returns the fresh groups and submission map
     */
    async exec(params: RefreshSubmissionsParams): Promise<RefreshSubmissionsResult> {
        // Fetch fresh worksheet submission groups from NorthStar
        const groups = await this.fetchGroupsOp.exec({ caseNumber: params.caseNumber });

        if (!groups) {
            return { groups: undefined, submissionMap: new Map() };
        }

        // Extract all submission IDs from the fresh groups
        const submissionIds: string[] = [];
        for (const group of groups) {
            for (const title of group.worksheetSubmissionTitles) {
                if (title.submissionId) {
                    submissionIds.push(title.submissionId);
                }
            }
        }

        // Bulk-fetch all submissions in parallel
        const submissionMap =
            submissionIds.length > 0
                ? await this.fetchAllSubmissionsOp.exec({ caseNumber: params.caseNumber, submissionIds })
                : new Map<string, WorksheetFormEntity>();

        return { groups, submissionMap };
    }
}
